import React, { useState } from "react";
import styled from "styled-components"
import { purpleColors, slateColors, Section, Container } from '../ContentPages/AppLandingPage/styles';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const FAQSection = styled(Section)`
  background: white;
  width: 100%;
  padding: 1.5rem 1rem;
  box-sizing: border-box;
  
  @media (min-width: 640px) {
    padding: 2rem 1.5rem;
  }
  
  @media (min-width: 768px) {
    padding: 2rem 3rem;
  }
`;

const FAQContainer = styled(Container)`
  max-width: 48rem;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1.25rem;
  text-align: center;
  
  @media (min-width: 640px) {
    font-size: 1.5rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.5rem;
  }
`;

const FAQList = styled.div`
display: flex;
flex-direction: column;
  gap: 0;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid ${slateColors[200]};
  
  &:last-child {
    border-bottom: none;
  }
`;

const QuestionButton = styled.button`
  width: 100%;
display: flex;
align-items: center;
  justify-content: space-between;
  padding: 0.875rem 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  
  &:hover {
    .question-text {
      color: ${purpleColors[600]};
    }
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    color: ${slateColors[400]};
    flex-shrink: 0;
    transition: transform 0.2s, color 0.2s;
  }
  
  &[aria-expanded="true"] {
    svg {
      color: ${purpleColors[600]};
    }
  }
`;

const QuestionText = styled.h3<{ $isExpanded: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin: 0;
  padding-right: 1rem;
  line-height: 1.5;
  transition: color 0.2s;
  
  @media (min-width: 640px) {
    font-size: 0.9375rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const AnswerContainer = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-out;
`;

const AnswerContent = styled.div`
  padding: 0 0 1rem 0;
  color: ${slateColors[600]};
  font-size: 0.8125rem;
  line-height: 1.5;
  
  @media (min-width: 640px) {
    font-size: 0.875rem;
  }
  
  ul {
    margin: 0.5rem 0;
    padding-left: 1.25rem;
    
    li {
      margin-bottom: 0.375rem;
    }
  }
  
  p {
    margin: 0;
  }
`;

const FAQ = () => {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    
    const faqItems = [
        {
            question: t('footerSection.faqs.trainsQuestion'),
            answer: (
                <>
          <p>{t('footerSection.faqs.trainsAnswer')}</p>
                    <ul>
                        <li>{t('footerSection.faqs.trainsList.germany')}</li>
                        <li>{t('footerSection.faqs.trainsList.netherlands')}</li>
                        <li>{t('footerSection.faqs.trainsList.belgium')}</li>
                        <li>{t('footerSection.faqs.trainsList.france')}</li>
                        <li>{t('footerSection.faqs.trainsList.switzerland')}</li>
                        <li>{t('footerSection.faqs.trainsList.austria')}</li>
                        <li>{t('footerSection.faqs.trainsList.italy')}</li>
                        <li>{t('footerSection.faqs.trainsList.denmark')}</li>
                        <li>{t('footerSection.faqs.trainsList.poland')}</li>
                        <li>{t('footerSection.faqs.trainsList.hungary')}</li>
                        <li>{t('footerSection.faqs.trainsList.uk')}</li>
                        <li>{t('footerSection.faqs.trainsList.other')}</li>
                    </ul>
                </>
            ),
        },
        {
            question: t('footerSection.faqs.freeQuestion'),
            answer: t('footerSection.faqs.freeAnswer'),
        },
        {
            question: t('footerSection.faqs.androidQuestion'),
            answer: t('footerSection.faqs.androidAnswer'),
        },
        {
            question: t('footerSection.faqs.bookingQuestion'),
            answer: t('footerSection.faqs.bookingAnswer'),
        },
        {
            question: t('footerSection.faqs.dataQuestion'),
            answer: t('footerSection.faqs.dataAnswer'),
        },
    ];

    const toggleAnswer = (index: number) => {
        setExpandedIndex(prevIndex => (prevIndex === index ? null : index));
    };

    return (
    <FAQSection>
      <FAQContainer>
        <Title>{t('footerSection.faqs.title')}</Title>
        <FAQList>
          {faqItems.map((item, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <FAQItem key={index}>
                <QuestionButton
                  onClick={() => toggleAnswer(index)}
                  aria-expanded={isExpanded}
                  aria-controls={`faq-answer-${index}`}
                >
                  <QuestionText className="question-text" $isExpanded={isExpanded}>
                            {item.question}
                  </QuestionText>
                  {isExpanded ? <ChevronUp /> : <ChevronDown />}
                </QuestionButton>
                <AnswerContainer
                  id={`faq-answer-${index}`}
                  $isExpanded={isExpanded}
                  role="region"
                  aria-hidden={!isExpanded}
                >
                  <AnswerContent>
                    {typeof item.answer === 'string' ? (
                      <p>{item.answer}</p>
                    ) : (
                      item.answer
                    )}
                  </AnswerContent>
                </AnswerContainer>
              </FAQItem>
            );
          })}
        </FAQList>
      </FAQContainer>
    </FAQSection>
    );
  };
  
  export default FAQ;
